<template>
  <div class="ai-chat-trace-step">
    <!-- MBtn emits on Enter itself and the native button clicks on Enter too: prevent keeps it one toggle -->
    <m-btn
      class="_header"
      :clickable="expandable"
      :aria-expanded="expandable ? open : undefined"
      light
      hide-border
      xs
      align-left
      @click="toggle"
      @keydown.enter.prevent
    >
      <m-icon
        v-if="!compact"
        class="_icon"
        :type="stepIcon(entry.step)"
        aria-hidden="true"
      />
      <span :class="['_label', entry.step.status === AI_CHAT_STEP_STATUS.running ? '-running' : '', entry.query !== null ? '-fixed' : '']">
        {{ entry.title ?? stepLabel(entry.step, { t, te }) }}
      </span>
      <span
        v-if="entry.query !== null"
        class="_secondary _query"
      >
        {{ entry.query }}
      </span>
      <span
        v-if="entry.count > 1"
        class="_secondary"
      >
        {{ $t('aiChat.trace.count', { count: entry.count }) }}
      </span>
      <span
        v-if="entry.step.status === AI_CHAT_STEP_STATUS.failed"
        class="_secondary"
      >
        {{ $t('aiChat.trace.failed') }}
      </span>
      <m-icon
        v-if="expandable"
        :type="open ? 'down' : 'right'"
        size="11"
        aria-hidden="true"
      />
    </m-btn>
    <div
      v-if="open"
      :class="['_details', compact ? '-compact' : '']"
    >
      <m-editor
        v-for="(thought, index) in entry.thoughts"
        :key="index"
        class="_detail"
        :initial-value="markdownToDoc(thought)"
        :allowed-content="allowedContent"
        disabled
        disable-mobile-menu
        hide-placeholder
      />
      <div
        v-if="entry.input !== ''"
        class="_detail -plain"
      >
        {{ entry.input }}
      </div>
    </div>
  </div>
</template>

<script setup>
import MEditor from '@/components/editor/MEditor.vue';
import { AI_CHAT_STEP_STATUS } from '@/lib/constants';
import { computed, ref } from 'vue';
import { editorNodeType } from 'shared/constants.json';
import { markdownToDoc } from '@/lib/markdown';
import { stepIcon, stepLabel } from '@/lib/ai-chat-trace';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  entry: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t, te } = useI18n();

const allowedContent = [editorNodeType.bulletList, editorNodeType.orderedList, editorNodeType.codeBlock];

const expandable = computed(() => props.entry.thoughts.length > 0 || props.entry.input !== '');

const open = ref(false);
const toggle = () => { open.value = !open.value; };
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  @keyframes shimmer {
    from {
      background-position: 100% 0;
    }

    to {
      background-position: 0 0;
    }
  }

  .ai-chat-trace-step {
    ._header {
      display: inline-flex;
      gap: .6rem;
      max-width: 100%;
      margin-left: -.6rem;
    }

    ._icon {
      flex: 0 0 auto;
    }

    ._label {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &.-running {
        color: transparent;
        background: linear-gradient(90deg, $font-color-tertiary 40%, $font-color-primary 50%, $font-color-tertiary 60%);
        background-clip: text;
        background-size: 300% 100%;
        animation: shimmer 2s linear infinite;

        @media (prefers-reduced-motion: reduce) {
          color: $font-color-tertiary;
          background: none;
          animation: none;
        }
      }

      &.-fixed {
        flex-shrink: 0;
      }
    }

    ._secondary {
      flex: 0 0 auto;
      color: $font-color-tertiary;
    }

    ._query {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    ._details {
      padding-left: 2.2rem;

      &.-compact {
        padding-left: 1.2rem;
        margin: .2rem 0 0 .2rem;
        border-left: 2px solid $border-color;
      }
    }

    ._detail {
      max-height: 24rem;
      padding: .4rem 0;
      overflow-y: auto;
      color: $font-color-tertiary;
      overflow-wrap: anywhere;

      &.-plain {
        white-space: pre-wrap;
      }
    }
  }
</style>
